import { useCommunityContext } from 'providers';
import { useQueryClient } from 'react-query';

import { HomeFeedPostKeyFilter, useTogglePostLikeMutation } from 'shared/graphql';
import { recordError } from 'shared/utils/record';

import { STREAM_CHAT_ENRICHED_ACTIVITY_QUERY_KEY } from './useStreamChatEnrichedActivity';

/**
 * Custom hook to toggle the like status of a post.
 *
 * Uses the `useTogglePostLikeMutation` GraphQL mutation to update the like status for a given post. Returns a `toggle` function that accepts a post object and triggers the mutation.
 *
 * @returns An object containing the `toggle` function to like/unlike a post.
 */
export function useTogglePostLike() {
  const { selectedCommunity } = useCommunityContext();

  const [mutate] = useTogglePostLikeMutation();

  const query = useQueryClient();

  const toggle = async (post: HomeFeedPostKeyFilter) => {
    if (!selectedCommunity) {
      throw new Error('Community not found');
    }

    try {
      await mutate({
        variables: {
          post: post.id!,
          community: selectedCommunity.id!,
        },
      });
    } catch (err) {
      recordError(err);
      throw err;
    } finally {
      // Invalidate the query to refetch the enriched activity data for the post.
      await query.invalidateQueries([STREAM_CHAT_ENRICHED_ACTIVITY_QUERY_KEY, post.id]);
    }
  };

  return {
    toggle,
  };
}
